import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { BrandLockup } from "@/components/LogoMark";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-xl font-bold text-[#0A192F] mb-3">{title}</h2>
    <div className="text-[15px] text-slate-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <BrandLockup />
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0A192F] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-3xl mx-auto px-5 sm:px-8 py-14"
      >
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck className="w-6 h-6 text-[#10B981]" />
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">GDPR · Reg. UE 2016/679</p>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0A192F] mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-12">Ultimo aggiornamento: 4 novembre 2026</p>

        <Section title="1. Titolare del trattamento">
          <p>
            Il Titolare del trattamento dei dati personali è <strong className="text-[#0A192F]">Casamatch.org — La casa cerca te!</strong>,
            raggiungibile all'indirizzo email <strong className="text-[#0A192F]">mediterraneumsrl@gmail.com</strong>.
          </p>
        </Section>

        <Section title="2. Dati personali raccolti">
          <p>Attraverso il modulo di richiesta raccogliamo esclusivamente i dati che ci fornisci volontariamente:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Nome e numero WhatsApp (dati di contatto);</li>
            <li>tipologia di ricerca (acquisto, affitto lungo, affitto turistico) e tipo di immobile;</li>
            <li>Provincia, comune o zona di interesse e budget massimo;</li>
            <li>Caratteristiche indispensabili dell'immobile richiesto.</li>
          </ul>
          <p>Per le agenzie immobiliari raccogliamo: nome dell'agenzia, zona di operatività, email, telefono ed eventuali note.</p>
        </Section>

        <Section title="3. Finalità e base giuridica del trattamento">
          <p>I tuoi dati sono trattati per le seguenti finalità:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-[#0A192F]">Servizio di matching immobiliare:</strong> incrociare la tua richiesta con i portafogli di agenzie e proprietari della zona scelta e farti ricontattare solo in caso di match pertinente (base giuridica: esecuzione di misure precontrattuali richieste dall'interessato, art. 6.1.b GDPR);</li>
            <li><strong className="text-[#0A192F]">Gestione e protezione della richiesta:</strong> conservazione sicura della richiesta nel nostro database (base giuridica: legittimo interesse, art. 6.1.f GDPR);</li>
            <li><strong className="text-[#0A192F]">Adempimenti di legge:</strong> ove richiesto dalla normativa vigente (art. 6.1.c GDPR).</li>
          </ul>
        </Section>

        <Section title="4. Modalità del trattamento e sicurezza">
          <p>
            I dati sono trattati con strumenti informatici, con misure tecniche e organizzative adeguate a garantire
            riservatezza e integrità. L'accesso al database è riservato esclusivamente al Titolare tramite area
            riservata protetta da credenziali.
          </p>
        </Section>

        <Section title="5. Comunicazione e cessione dei dati a terzi">
          <p>
            I tuoi dati <strong className="text-[#0A192F]">non vengono mai diffusi pubblicamente</strong>: nella bacheca
            delle richieste sono visibili solo tipologia, zona, budget e caratteristiche, mai nome o contatti.
          </p>
          <p>
            <strong className="text-[#0A192F]">Vendita del contatto (sblocco):</strong> il tuo nome, numero WhatsApp ed
            email possono essere ceduti, previo pagamento da parte del destinatario, ad agenzie immobiliari,
            operatori del settore o privati interessati a proporti un immobile o una soluzione abitativa
            (base giuridica: consenso espresso con l'invio del modulo, art. 6.1.a GDPR). I destinatari sono tenuti
            a usare i tuoi dati solo per proporti la loro offerta e a non cederli ulteriormente.
          </p>
          <p>
            <strong className="text-[#0A192F]">Valutazione di pertinenza:</strong> la scelta di contattarti e la
            valutazione sull'aderenza dell'immobile alla tua richiesta sono effettuate autonomamente dal destinatario
            del contatto. Casamatch.org raccoglie e trasmette le richieste così come inserite e{" "}
            <strong className="text-[#0A192F]">non garantisce né verifica</strong> che l'immobile proposto
            corrisponda ai criteri da te indicati.
          </p>
          <p>
            <strong className="text-[#0A192F]">Comunicazioni successive:</strong> potrai ricevere via email aggiornamenti
            sull'esito della tua richiesta (es. conferma di ricezione, richiesta di riscontro sull'andamento del
            contatto). Non riceverai mai pubblicità di terzi né spam: i mittenti pagano per contattarti e questo è il
            nostro filtro anti-spam.
          </p>
          <p>
            In nessun caso i dati sono venduti a liste di marketing, call center o soggetti senza un immobile
            pertinente alla tua richiesta.
          </p>
        </Section>

        <Section title="6. Natura del servizio (no mediazione immobiliare)">
          <p>
            Casamatch.org è una piattaforma di incontro tra domanda e offerta: raccoglie le richieste di chi cerca
            casa e mette in contatto le parti. <strong className="text-[#0A192F]">Non svolge attività di mediazione
            immobiliare</strong> ai sensi della Legge 39/1989: non partecipa alle trattative, non conclude affari,
            non percepisce provvigioni sulle compravendite o locazioni e non verifica gli immobili né l'identità dei
            soggetti. La trattativa, le visite e ogni accordo avvengono esclusivamente tra te e chi ti contatta, che
            resta l'unico responsabile delle proposte che ti rivolge.
          </p>
        </Section>

        <Section title="7. Periodo di conservazione">
          <p>
            I dati della richiesta sono conservati per il tempo necessario a gestire il matching e comunque non oltre
            24 mesi dall'ultimo contatto, salvo tua richiesta di cancellazione anticipata. I dati delle agenzie sono
            conservati per la durata del rapporto commerciale.
          </p>
        </Section>

        <Section title="8. Diritti dell'interessato">
          <p>Ai sensi degli artt. 15-22 GDPR hai diritto di:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>accedere ai tuoi dati personali e ottenerne copia;</li>
            <li>ottenere la rettifica o la cancellazione dei dati;</li>
            <li>limitare o opporti al trattamento;</li>
            <li>ottenere la portabilità dei dati;</li>
            <li>revocare il consenso in qualsiasi momento;</li>
            <li>proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).</li>
          </ul>
          <p>
            Per esercitare i tuoi diritti scrivi a <strong className="text-[#0A192F]">mediterraneumsrl@gmail.com</strong>:
            risponderemo entro 30 giorni.
          </p>
        </Section>

        <Section title="9. Cookie e strumenti di tracciamento">
          <p>
            Questo sito utilizza cookie tecnici necessari al funzionamento (area riservata del Titolare, memorizzazione
            nel browser dei contatti sbloccati dopo il pagamento) e uno strumento di analisi anonima del traffico
            (PostHog) per migliorare il servizio. Non vengono utilizzati cookie di profilazione pubblicitaria.
            Il dettaglio è nella <a href="/cookie-policy" className="font-semibold text-[#0A192F] underline underline-offset-2">Cookie Policy</a>.
          </p>
        </Section>

        <Section title="10. Modifiche alla presente informativa">
          <p>
            Il Titolare può aggiornare questa informativa in qualsiasi momento. La versione vigente è quella
            pubblicata su questa pagina con l'indicazione della data di ultimo aggiornamento.
          </p>
        </Section>
      </motion.main>
    </div>
  );
}
